import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import { useI18n } from '../../features/i18n';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/safety-signals — community-reported allergy-safe dishes, rebuilt
 * in brand primitives (Nunito, CWM tokens, pill buttons, sheet cards, mascot
 * backdrop). Replaces the MUI recovery screen at this route.
 */

// Verification + evidence labels translate at render time via *_KEY maps.
// The English fallbacks stay for any call-site that receives just the raw
// value (e.g. future API responses not yet wired to useI18n).
const VERIFICATION_META = {
  unverified:           { bg: '#F3F4F6', fg: '#6B7280', dot: '#9CA3AF', labelKey: 'safety.ver_unverified' },
  restaurant_verified:  { bg: '#E6FBEF', fg: '#0F7A3A', dot: '#16803C', labelKey: 'safety.ver_restaurant' },
  staff_verified:       { bg: '#E5F3FF', fg: '#0F5FB8', dot: '#0F7FE8', labelKey: 'safety.ver_staff' },
  kitchen_confirmed:    { bg: '#FFF3D6', fg: '#8A4A00', dot: '#B45309', labelKey: 'safety.ver_kitchen' },
};

const EVIDENCE_LABEL_KEY = {
  menu_label:        'safety.evidence_menu_label',
  server_confirmed:  'safety.evidence_server_confirmed',
  kitchen_confirmed: 'safety.evidence_kitchen_confirmed',
  user_experience:   'safety.evidence_user_experience',
};

const COMMON_RESTRICTIONS = [
  'Gluten-Free', 'Dairy-Free', 'Nut-Free', 'Vegan', 'Vegetarian',
  'Shellfish-Free', 'Egg-Free', 'Soy-Free', 'Kosher', 'Halal',
];

// Backend signal -> card shape.
const toSignal = (s) => ({
  ...s,
  id: s.signal_id,
  report_type: s.report_type || 'positive',
  restrictions_met: Array.isArray(s.restrictions_met) ? s.restrictions_met : [],
  is_anonymous: s.attribution === 'anonymous',
});

export default function UnifiedSafetySignals() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [filterRestaurantId, setFilterRestaurantId] = useState('all');
  const [filterRestriction, setFilterRestriction] = useState('all');
  const [filterType, setFilterType] = useState('all');  // 'all' | 'positive' | 'negative'
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());

  // Signals are visible to their author and accepted table members only.
  const load = useCallback(async () => {
    const [list, live] = await Promise.all([
      dataService.listRestaurants().catch(() => []),
      dataService.listSafetySignals().catch(() => null),
    ]);
    setRestaurants(Array.isArray(list) ? list : (list?.restaurants || []));
    if (live === null) { setError(t('safety.load_failed')); setSignals([]); return; }
    setSignals(live.map(toSignal));
  }, [t]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    setLoading(true);
    load().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate, load]);

  const filtered = useMemo(() => signals.filter((s) => {
    if (filterRestaurantId !== 'all' && String(s.restaurant_id) !== String(filterRestaurantId)) return false;
    if (filterRestriction !== 'all' && !s.restrictions_met.includes(filterRestriction)) return false;
    if (filterType !== 'all' && (s.report_type || 'positive') !== filterType) return false;
    return true;
  }), [signals, filterRestaurantId, filterRestriction, filterType]);

  const submitSignal = async () => {
    if (!form.restaurant_id) { setError(t('safety.err_pick_restaurant')); return; }
    if (!form.dish_name.trim()) { setError(t('safety.err_name_dish')); return; }
    if (form.restrictions_met.length === 0) { setError(t('safety.err_pick_restriction')); return; }
    if (form.report_type === 'positive' && !form.what_worked.trim()) { setError(t('safety.err_describe_worked')); return; }
    if (form.report_type === 'negative' && !form.what_happened.trim()) { setError(t('safety.err_describe_happened')); return; }
    if (form.report_type === 'negative' && !form.severity) { setError(t('safety.err_pick_severity')); return; }

    try {
      const negative = form.report_type === 'negative';
      await dataService.createSafetySignal({
        restaurant_id: Number(form.restaurant_id),
        report_type: form.report_type,
        severity: negative ? form.severity : undefined,
        dish_name: form.dish_name,
        restrictions_met: form.restrictions_met,
        what_worked: negative ? undefined : form.what_worked,
        what_happened: negative ? form.what_happened : undefined,
        notes: form.notes || undefined,
        verification_state: form.verification_state,
        evidence_type: form.evidence_type,
        confidence: form.confidence,
        visibility: form.visibility,
        attribution: form.is_anonymous ? 'anonymous' : 'attributed',
      });
      setSuccess(t('safety.share_success'));
      setDialogOpen(false);
      setForm(emptyForm());
      setTimeout(() => setSuccess(null), 3000);
      await load();
    } catch (e) {
      setError(e?.message || t('safety.err_share_failed'));
    }
  };

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <ShieldIcon size={12} /> {t('safety.eyebrow')}
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            {t('safety.title_l1')} <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{t('safety.title_l2')}</em>.
          </h1>
          <p style={{ maxWidth: 580, margin: '0 auto', fontSize: 15, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            {t('safety.subtitle')}
          </p>
          <button className="cwm-btn" style={{ marginTop: 20 }} onClick={() => setDialogOpen(true)}>
            <PlusIcon size={14} /> {t('safety.share_cta')}
          </button>
        </motion.section>

        {/* Alerts */}
        <AnimatePresence>
          {(error || success) && (
            <motion.div
              key={error ? 'err' : 'ok'}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              style={{
                padding: '12px 16px',
                borderRadius: 14,
                marginBottom: 16,
                background: error ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
                color: error ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
                fontWeight: 700, fontSize: 14,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <span>{error || success}</span>
              <button
                onClick={() => { setError(null); setSuccess(null); }}
                style={{ background: 'transparent', border: 'none', color: 'inherit', fontWeight: 900, cursor: 'pointer', fontSize: 16 }}
                aria-label="dismiss"
              >×</button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filters */}
        <div className="cwm-sheet" style={{ padding: 14, marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--cwm-gray-700)', fontWeight: 800, fontSize: 13 }}>
            <FilterIcon size={14} /> {t('safety.filter_label')}
          </div>

          {/* Type: All / Worked / Reactions — labels from t() so we don't
              shadow the translator inside the .map callback. */}
          <div style={{ display: 'inline-flex', gap: 4, padding: 3, background: 'var(--cwm-gray-50)', borderRadius: 999, border: '1.5px solid var(--cwm-gray-100)' }}>
            {[
              { id: 'all',      label: t('safety.filter_all'),       count: signals.length },
              { id: 'positive', label: t('safety.filter_worked'),    count: signals.filter((s) => (s.report_type || 'positive') === 'positive').length },
              { id: 'negative', label: t('safety.filter_reactions'), count: signals.filter((s) => s.report_type === 'negative').length },
            ].map((opt) => {
              const on = filterType === opt.id;
              const tone = opt.id === 'negative' ? 'red' : opt.id === 'positive' ? 'green' : 'blue';
              const colorMap = {
                red:   { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
                green: { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: '#16803C' },
                blue:  { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
              };
              const c = colorMap[tone];
              return (
                <button key={opt.id} type="button" onClick={() => setFilterType(opt.id)}
                        style={{
                          padding: '6px 12px', borderRadius: 999,
                          background: on ? c.solid : 'transparent',
                          color: on ? '#fff' : c.fg,
                          border: 'none', fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 900,
                          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                  {opt.label}
                  <span style={{
                    padding: '1px 6px', borderRadius: 999,
                    background: on ? 'rgba(255,255,255,0.22)' : c.bg,
                    color: on ? '#fff' : c.fg,
                    fontSize: 10, fontWeight: 900,
                  }}>{opt.count}</span>
                </button>
              );
            })}
          </div>

          <SelectPill
            value={filterRestaurantId}
            onChange={setFilterRestaurantId}
            options={[{ value: 'all', label: t('safety.filter_all_restaurants') }, ...restaurants.map((r) => ({ value: String(r.id), label: r.name }))]}
          />
          <SelectPill
            value={filterRestriction}
            onChange={setFilterRestriction}
            options={[{ value: 'all', label: t('safety.filter_all_restrictions') }, ...COMMON_RESTRICTIONS.map((r) => ({ value: r, label: r }))]}
          />
          <div style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 800, color: 'var(--cwm-gray-500)' }}>
            {t('safety.results_count', { shown: filtered.length, total: signals.length })}
          </div>
        </div>

        {/* Signal list */}
        {loading ? (
          <SignalSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState t={t} onShare={() => setDialogOpen(true)} />
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            {filtered.map((s, i) => (
              <SignalCard key={s.id} t={t} signal={s} index={i} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {dialogOpen && (
          <CreateSignalDialog
            t={t}
            form={form}
            setForm={setForm}
            restaurants={restaurants}
            onClose={() => { setDialogOpen(false); setError(null); }}
            onSubmit={submitSignal}
          />
        )}
      </AnimatePresence>
    </AppShell>
  );
}

// ---------- sub-components ----------

function SignalCard({ t, signal, index }) {
  const ver = VERIFICATION_META[signal.verification_state] || VERIFICATION_META.unverified;
  const verLabel = t(ver.labelKey);
  const evidenceKey = EVIDENCE_LABEL_KEY[signal.evidence_type];
  const evidence = evidenceKey ? t(evidenceKey) : signal.evidence_type;
  const date = new Date(signal.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
      className="cwm-sheet"
      style={{ padding: 20 }}
    >
      {signal.report_type === 'negative' && (
        <div style={{
          margin: '-20px -20px 14px', padding: '8px 20px',
          background: (signal.severity === 4 ? '#1F2937' : 'var(--cwm-red-light)'),
          color: (signal.severity === 4 ? '#fff' : 'var(--cwm-red-text)'),
          fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderRadius: '18px 18px 0 0',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <AlertIcon size={12} /> {t('safety.reaction_report')}
          </span>
          {signal.severity && (
            <span>{(() => {
              const tier = SEVERITY_TIERS.find((x) => x.id === signal.severity);
              return tier ? t(tier.labelKey) : '';
            })()}</span>
          )}
        </div>
      )}

      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 900, letterSpacing: '0.1em', color: 'var(--cwm-blue-deep)', textTransform: 'uppercase', marginBottom: 4 }}>
            <ForkIcon size={12} /> {signal.restaurant_name}
          </div>
          <h3 style={{ fontSize: 19, fontWeight: 900, margin: 0, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>
            {signal.dish_name}
          </h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          {signal.report_type !== 'negative' && <VerificationBadge meta={ver} label={verLabel} />}
          {signal.report_type !== 'negative' && <StarRating value={signal.confidence} />}
        </div>
      </header>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {signal.restrictions_met.map((r) => (
          <span key={r} className="cwm-tag" style={{
            background: signal.report_type === 'negative' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
            color:      signal.report_type === 'negative' ? 'var(--cwm-red-text)'  : 'var(--cwm-green-text)',
            border:     `1px solid ${signal.report_type === 'negative' ? '#F4B6B6' : '#A6E8C2'}`,
          }}>
            {signal.report_type === 'negative' ? '⚠' : '✓'} {r}
          </span>
        ))}
      </div>

      <p style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.55, color: 'var(--cwm-gray-700)' }}>
        {signal.report_type === 'negative' ? signal.what_happened : signal.what_worked}
      </p>

      {signal.notes && (
        <p style={{ margin: '0 0 12px', padding: '10px 12px', background: '#F7FBFF', borderLeft: '3px solid var(--cwm-blue)', borderRadius: '0 10px 10px 0', fontSize: 13, color: 'var(--cwm-gray-700)', fontStyle: 'italic' }}>
          {signal.notes}
        </p>
      )}

      <footer style={{ paddingTop: 10, borderTop: '1px solid var(--cwm-gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
        <span>
          <span style={{ padding: '3px 8px', borderRadius: 999, background: ver.bg, color: ver.fg, marginRight: 8, fontSize: 10, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {evidence}
          </span>
          {signal.user_name ? t('safety.card_by', { name: signal.user_name }) : t('safety.card_anon')}
        </span>
        <span>{date}</span>
      </footer>
    </motion.article>
  );
}

function VerificationBadge({ meta, label }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: meta.bg, color: meta.fg,
      fontSize: 11, fontWeight: 900, letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: meta.dot }} />
      {label}
    </span>
  );
}

function StarRating({ value, max = 5 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {Array.from({ length: max }).map((_, i) => (
        <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill={i < value ? '#F5A623' : 'none'} stroke={i < value ? '#F5A623' : '#DADCE0'} strokeWidth="2" strokeLinejoin="round">
          <path d="M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/>
        </svg>
      ))}
    </span>
  );
}

function SelectPill({ value, onChange, options }) {
  return (
    <label style={{ position: 'relative', display: 'inline-block' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          appearance: 'none',
          padding: '10px 36px 10px 16px',
          borderRadius: 999,
          border: '1.5px solid var(--cwm-blue-light)',
          background: '#fff',
          color: 'var(--cwm-blue-deep)',
          fontFamily: 'var(--cwm-font)',
          fontSize: 13, fontWeight: 800,
          cursor: 'pointer',
          minWidth: 180,
        }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
           style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--cwm-blue-deep)', pointerEvents: 'none' }}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </label>
  );
}

function SignalSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="cwm-sheet" style={{ padding: 20, opacity: 0.65 }}>
          <div style={{ height: 14, width: 160, background: 'var(--cwm-gray-100)', borderRadius: 6, marginBottom: 10 }}/>
          <div style={{ height: 22, width: 220, background: 'var(--cwm-gray-100)', borderRadius: 8, marginBottom: 14 }}/>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            <div style={{ height: 22, width: 80, background: 'var(--cwm-gray-100)', borderRadius: 999 }}/>
            <div style={{ height: 22, width: 80, background: 'var(--cwm-gray-100)', borderRadius: 999 }}/>
          </div>
          <div style={{ height: 48, background: 'var(--cwm-gray-100)', borderRadius: 10 }}/>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ t, onShare }) {
  return (
    <div className="cwm-sheet" style={{ padding: '44px 24px', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, margin: '0 auto 14px', borderRadius: '50%', background: 'var(--cwm-blue-50)', display: 'grid', placeItems: 'center', color: 'var(--cwm-blue-deep)' }}>
        <ShieldIcon size={28} />
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 900, margin: '0 0 6px', color: 'var(--cwm-ink)' }}>
        {t('safety.empty_title')}
      </h3>
      <p style={{ maxWidth: 420, margin: '0 auto 18px', fontSize: 14, color: 'var(--cwm-gray-500)' }}>
        {t('safety.empty_body')}
      </p>
      <button className="cwm-btn" onClick={onShare}>
        <PlusIcon size={14} /> {t('safety.share_cta')}
      </button>
    </div>
  );
}

function CreateSignalDialog({ t, form, setForm, restaurants, onClose, onSubmit }) {
  const toggleRestriction = (r) => setForm({ ...form, restrictions_met:
    form.restrictions_met.includes(r)
      ? form.restrictions_met.filter((x) => x !== r)
      : [...form.restrictions_met, r]
  });

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10,37,64,0.55)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <motion.div
        initial={{ scale: 0.96, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 28, padding: 26, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--cwm-shadow-hero)' }}
      >
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 18 }}>
          <div>
            <div className="cwm-eyebrow-badge" style={{ marginBottom: 8 }}>
              <ShieldIcon size={12} /> {t('safety.share_dialog_eyebrow')}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 900, margin: 0, letterSpacing: '-0.01em', color: 'var(--cwm-ink)' }}>
              {form.report_type === 'negative' ? t('safety.share_dialog_title_negative') : t('safety.share_dialog_title_positive')}
            </h2>
          </div>
          <button onClick={onClose} aria-label="close"
                  style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--cwm-gray-100)', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 900, color: 'var(--cwm-gray-700)' }}>×</button>
        </header>

        {/* Report type toggle — renamed .map callback to opt to avoid
            shadowing the translator `t`. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {[
            { id: 'positive', label: t('safety.type_worked'),   hint: t('safety.type_worked_hint'), icon: 'check',   tone: 'green' },
            { id: 'negative', label: t('safety.type_wrong'),    hint: t('safety.type_wrong_hint'),  icon: 'warning', tone: 'red'   },
          ].map((opt) => {
            const on = form.report_type === opt.id;
            const palette = opt.tone === 'green'
              ? { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: '#16803C', border: '#A6E8C2' }
              : { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C', border: '#F4B6B6' };
            return (
              <button key={opt.id} type="button"
                      onClick={() => setForm({ ...form, report_type: opt.id })}
                      style={{
                        padding: '12px 14px', borderRadius: 16,
                        background: on ? palette.bg : '#fff',
                        color: on ? palette.fg : 'var(--cwm-gray-700)',
                        border: `1.5px solid ${on ? palette.solid : 'var(--cwm-gray-200)'}`,
                        fontFamily: 'var(--cwm-font)', cursor: 'pointer',
                        textAlign: 'left',
                        boxShadow: on ? `0 4px 12px ${palette.solid}22` : 'none',
                        transition: 'all 140ms ease',
                      }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ width: 20, height: 20, borderRadius: 999, background: palette.solid, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 900 }}>
                    {opt.icon === 'check' ? '✓' : '!'}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 900 }}>{opt.label}</span>
                </div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: on ? palette.fg : 'var(--cwm-gray-500)' }}>{opt.hint}</div>
              </button>
            );
          })}
        </div>

        <Field label={t('safety.field_restaurant')}>
          <SelectPill
            value={form.restaurant_id || ''}
            onChange={(v) => setForm({ ...form, restaurant_id: v || null })}
            options={[{ value: '', label: t('safety.restaurant_placeholder') }, ...restaurants.map((r) => ({ value: String(r.id), label: r.name }))]}
          />
        </Field>

        <Field label={t('safety.field_dish')}>
          <BrandInput
            value={form.dish_name}
            onChange={(v) => setForm({ ...form, dish_name: v })}
            placeholder={t('safety.dish_placeholder')}
          />
        </Field>

        <Field label={t('safety.field_restrictions')}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {COMMON_RESTRICTIONS.map((r) => {
              const on = form.restrictions_met.includes(r);
              return (
                <button key={r} type="button" onClick={() => toggleRestriction(r)}
                        style={{
                          padding: '7px 14px', borderRadius: 999,
                          border: `1.5px solid ${on ? '#16803C' : '#A6E8C2'}`,
                          background: on ? '#16803C' : 'var(--cwm-green-light)',
                          color: on ? '#fff' : 'var(--cwm-green-text)',
                          fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                          cursor: 'pointer', transition: 'all 140ms ease',
                        }}>
                  {on ? '✓ ' : ''}{r}
                </button>
              );
            })}
          </div>
        </Field>

        {form.report_type === 'negative' && (
          <Field label={t('safety.severity')}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
              {SEVERITY_TIERS.map((tier) => {
                const on = form.severity === tier.id;
                return (
                  <button key={tier.id} type="button"
                          onClick={() => setForm({ ...form, severity: tier.id })}
                          style={{
                            padding: '10px 12px', borderRadius: 12,
                            background: on ? tier.fg : tier.bg,
                            color: on ? (tier.id === 4 ? '#fff' : '#fff') : tier.fg,
                            border: `1.5px solid ${on ? tier.fg : tier.border}`,
                            fontFamily: 'var(--cwm-font)', cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'all 140ms ease',
                          }}>
                    <div style={{ fontSize: 12.5, fontWeight: 900 }}>{t(tier.labelKey)}</div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, opacity: 0.85 }}>{t(tier.hintKey)}</div>
                  </button>
                );
              })}
            </div>
          </Field>
        )}

        <Field label={form.report_type === 'negative' ? t('safety.what_happened_label') : t('safety.what_worked_label')}>
          <BrandTextArea
            value={form.report_type === 'negative' ? form.what_happened : form.what_worked}
            onChange={(v) => setForm({ ...form, [form.report_type === 'negative' ? 'what_happened' : 'what_worked']: v })}
            placeholder={form.report_type === 'negative'
              ? t('safety.what_happened_placeholder')
              : t('safety.what_worked_placeholder')}
          />
        </Field>

        <Field label={t('safety.notes_label')}>
          <BrandTextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            placeholder={t('safety.notes_placeholder')}
            rows={2}
          />
        </Field>

        {form.report_type === 'positive' && (
          <Field label={t('safety.confidence')}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, confidence: n })}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill={n <= form.confidence ? '#F5A623' : 'none'} stroke={n <= form.confidence ? '#F5A623' : '#DADCE0'} strokeWidth="2" strokeLinejoin="round">
                    <path d="M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/>
                  </svg>
                </button>
              ))}
            </div>
          </Field>
        )}

        <Field label={t('safety.evidence')}>
          <SelectPill
            value={form.evidence_type}
            onChange={(v) => setForm({ ...form, evidence_type: v })}
            options={Object.entries(EVIDENCE_LABEL_KEY).map(([value, labelKey]) => ({ value, label: t(labelKey) }))}
          />
        </Field>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700, color: 'var(--cwm-gray-700)', cursor: 'pointer', marginTop: 16 }}>
          <input
            type="checkbox"
            checked={form.is_anonymous}
            onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })}
            style={{ width: 18, height: 18, accentColor: 'var(--cwm-blue-deep)' }}
          />
          {t('safety.anonymous')}
        </label>

        <footer style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button className="cwm-btn-secondary" type="button" onClick={onClose}>{t('safety.cancel')}</button>
          <button className="cwm-btn" type="button" onClick={onSubmit}>{t('safety.submit')}</button>
        </footer>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>{label}</div>
      {children}
    </div>
  );
}

function BrandInput({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '12px 16px',
        borderRadius: 14,
        border: '1.5px solid var(--cwm-blue-light)',
        background: '#F7FBFF',
        color: 'var(--cwm-ink)',
        fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
        outline: 'none',
      }}
    />
  );
}

function BrandTextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '12px 16px',
        borderRadius: 14,
        border: '1.5px solid var(--cwm-blue-light)',
        background: '#F7FBFF',
        color: 'var(--cwm-ink)',
        fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.5,
        outline: 'none',
        resize: 'vertical',
      }}
    />
  );
}



// ---------- icons ----------
function ShieldIcon({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/></svg>; }
function PlusIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function ForkIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 2v6a3 3 0 003 3M17 2v6a3 3 0 01-3 3M12 11v11"/></svg>; }
function FilterIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18M6 12h12M10 19h4"/></svg>; }
function AlertIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L1 21h22zM12 9v6M12 18h.01"/></svg>; }

// ---------- data ----------
function emptyForm() {
  return {
    report_type: 'positive',       // 'positive' = what worked | 'negative' = reaction
    severity: null,                // 1=mild, 2=moderate, 3=severe, 4=life-threatening (negative only)
    restaurant_id: null,
    dish_name: '',
    restrictions_met: [],
    what_worked: '',               // for positive
    what_happened: '',             // for negative
    notes: '',
    verification_state: 'unverified',
    evidence_type: 'user_experience',
    confidence: 3,
    visibility: 'table_only',      // 'table_only' | 'private'
    is_anonymous: false,
  };
}

const SEVERITY_TIERS = [
  { id: 1, labelKey: 'safety.sev_mild',     hintKey: 'safety.sev_mild_hint',     bg: '#FFF7E6', fg: '#8A4A00', border: '#F2D08A' },
  { id: 2, labelKey: 'safety.sev_moderate', hintKey: 'safety.sev_moderate_hint', bg: '#FFE9CC', fg: '#7A3A00', border: '#F2BC6A' },
  { id: 3, labelKey: 'safety.sev_severe',   hintKey: 'safety.sev_severe_hint',   bg: 'var(--cwm-red-light)', fg: 'var(--cwm-red-text)', border: '#F4B6B6' },
  { id: 4, labelKey: 'safety.sev_life',     hintKey: 'safety.sev_life_hint',     bg: '#1F2937', fg: '#fff', border: '#1F2937' },
];

// Plausible demo data — keeps the screen useful for design review until the
// backend endpoint lands. Mirrors the shape the recovery screen shipped with.
